// Sign-up moments, the browser's state (#5195; founder pick A, 2026-10-23).
// The words and the registry live in content/signupMoments.js; this file
// holds the three small records a moment needs and the one server call.
//
//   the moment record   sessionStorage `signup_moment_v1`, written when a
//                       moment's button is tapped: {moment, entry, school_ids,
//                       gpa?, state?}. It rides the email link with the rest
//                       of the tab's carry (emailLinkSignIn.sessionCarryKeys),
//                       labels signed_up / signin_completed with its entry,
//                       goes to the account's first get-credits so the
//                       server's account_created counts the moment, and fills
//                       /start (the schools and the GPA, offered, never saved
//                       or added without the student's tap: invariant 10).
//   the visit           sessionStorage `signup_visit_v1`: the college pages
//                       opened this visit (newest first, at most 3) with the
//                       band where the free read ran there, for the comparing
//                       strip; and the GPA and Targets count of the last read.
//   dismissed           sessionStorage `signup_dismissed_v1`: a moment's
//                       "Not now" holds for the visit.
//
// forgetVisitorDraft (an under-13 answer, a crisis) clears all three.
import { MOMENT_IDS, MOMENTS } from '../content/signupMoments';

export const MOMENT_KEY = 'signup_moment_v1';
export const VISIT_KEY = 'signup_visit_v1';
export const DISMISSED_KEY = 'signup_dismissed_v1';
// /start offers three colleges (StartPage MAX_COLLEGES), so a visit keeps
// three: the strip never names a school the first screen cannot offer.
export const MAX_COLLEGES = 3;
// How long a moment waits for its taste before it shows what the read holds.
export const PAYOFF_TIMEOUT_MS = 8000;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const SLUG_RE = /^[a-z0-9-]{1,96}$/;

const TRY_URL = import.meta.env.VITE_TRY_HERO_URL
    || `${import.meta.env.VITE_PROFILE_MANAGER_V2_URL || 'https://profile-manager-v2-pfnwjfp26a-ue.a.run.app'}/try-instant-read`;

const ss = {
    get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
    del(k) { try { sessionStorage.removeItem(k); } catch { /* nothing kept */ } },
};
const parse = (raw) => { try { return JSON.parse(raw || 'null'); } catch { return null; } };

/**
 * The moment's free taste from the server (profile_manager_v2
 * signup_moments: the band at every GPA, the Targets count and the first
 * one's name; band-level only). `fields`: the picture the free read was
 * signed over. Resolves the payoff or null; never throws (a moment without
 * its taste simply shows less).
 */
export async function momentPayoff(fields, schoolIds, payoffs) {
    if (!fields || typeof fields !== 'object' || !payoffs?.length) return null;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), PAYOFF_TIMEOUT_MS) : null;
    try {
        const res = await fetch(TRY_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fields, moment: { school_ids: (schoolIds || []).filter((i) => ID_RE.test(i)), payoffs } }),
            ...(ctl ? { signal: ctl.signal } : {}),
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data && data.success && data.moment && typeof data.moment === 'object' ? data.moment : null;
    } catch {
        return null;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

// ── the moment record ───────────────────────────────────────────────────────

/** The record, validated, or null. `raw`: a stored copy of it (the email
 *  link's snapshot); by default this tab's. */
export function loadMoment(raw = ss.get(MOMENT_KEY)) {
    const r = parse(raw);
    if (!r || !MOMENT_IDS.includes(r.moment)) return null;
    return {
        moment: r.moment,
        entry: MOMENTS[r.moment].entry,
        school_ids: Array.isArray(r.school_ids) ? r.school_ids.filter((i) => typeof i === 'string' && ID_RE.test(i)).slice(0, MAX_COLLEGES) : [],
        ...(typeof r.gpa === 'string' && /^\d(?:\.\d{1,2})?$/.test(r.gpa) ? { gpa: r.gpa } : {}),
        ...(typeof r.state === 'string' && /^[A-Z]{2}$/.test(r.state) ? { state: r.state } : {}),
        targets: r.targets === true,
    };
}

/** Written when a moment's button is tapped, before the sheet opens. */
export function holdMoment({ moment, schoolIds = [], gpa = null, state = null, targets = false }) {
    if (!MOMENT_IDS.includes(moment)) return;
    ss.set(MOMENT_KEY, JSON.stringify({
        moment,
        school_ids: schoolIds.filter((i) => typeof i === 'string' && ID_RE.test(i)).slice(0, MAX_COLLEGES),
        ...(gpa != null ? { gpa: String(gpa) } : {}),
        ...(state ? { state } : {}),
        targets: Boolean(targets),
    }));
}

/** A moment id and schools from an email link opened in another browser
 *  (emailLinkSignIn.readUrlCarry). Never over a record this tab holds. */
export function restoreMoment({ moment, schoolIds = [] }) {
    if (ss.get(MOMENT_KEY) != null || !MOMENT_IDS.includes(moment)) return;
    holdMoment({ moment, schoolIds });
}

export const clearMoment = () => ss.del(MOMENT_KEY);

/** The entry the record names, or null: signed_up and signin_completed
 *  carry it. */
export function heldMomentEntry() {
    const m = loadMoment();
    return m ? m.entry : null;
}

/** The record's moment id, or null: the account's first get-credits sends
 *  it for the server's account_created. */
export function heldMomentId() {
    const m = loadMoment();
    return m ? m.moment : null;
}

// ── dismissed for the visit ─────────────────────────────────────────────────

export function isDismissed(moment) {
    const d = parse(ss.get(DISMISSED_KEY));
    return Array.isArray(d) && d.includes(moment);
}

export function dismissMoment(moment) {
    const d = parse(ss.get(DISMISSED_KEY));
    const list = Array.isArray(d) ? d.filter((x) => MOMENT_IDS.includes(x)) : [];
    if (MOMENT_IDS.includes(moment) && !list.includes(moment)) list.push(moment);
    ss.set(DISMISSED_KEY, JSON.stringify(list));
}

// ── the visit's colleges ────────────────────────────────────────────────────

/** {colleges: [{slug, id, short, band?}], gpa?, targetsN?} for this visit. */
export function loadVisit() {
    const v = parse(ss.get(VISIT_KEY)) || {};
    const colleges = (Array.isArray(v.colleges) ? v.colleges : [])
        .filter((c) => c && SLUG_RE.test(c.slug) && ID_RE.test(c.id) && typeof c.short === 'string')
        .slice(0, MAX_COLLEGES)
        .map((c) => ({ slug: c.slug, id: c.id, short: c.short.slice(0, 60),
            ...(['Safety', 'Target', 'Reach'].includes(c.band) ? { band: c.band } : {}) }));
    return {
        colleges,
        ...(typeof v.gpa === 'string' ? { gpa: v.gpa } : {}),
        ...(Number.isInteger(v.targetsN) && v.targetsN >= 0 ? { targetsN: v.targetsN } : {}),
    };
}

function saveVisit(v) { ss.set(VISIT_KEY, JSON.stringify(v)); }

/** A college page opened: it moves to the front of the visit's list. */
export function noteCollegeVisit({ slug, id, short }) {
    if (!SLUG_RE.test(slug || '') || !ID_RE.test(id || '')) return loadVisit();
    const v = loadVisit();
    const had = v.colleges.find((c) => c.slug === slug);
    v.colleges = [{ ...(had || {}), slug, id, short }, ...v.colleges.filter((c) => c.slug !== slug)].slice(0, MAX_COLLEGES);
    saveVisit(v);
    return v;
}

/** The free read ran at a college: its band, and the picture's GPA and
 *  Targets count, for the strip on the next college page. */
export function noteCollegeRead({ slug, band, gpa = null, targetsN = null }) {
    const v = loadVisit();
    v.colleges = v.colleges.map((c) => (c.slug === slug ? { ...c, band } : c));
    if (gpa != null) v.gpa = String(gpa);
    if (Number.isInteger(targetsN)) v.targetsN = targetsN;
    saveVisit(v);
}

/** Everything a moment keeps in this tab goes (forgetVisitorDraft). */
export function forgetMoments() {
    [MOMENT_KEY, VISIT_KEY, DISMISSED_KEY].forEach((k) => ss.del(k));
}
